import { baseApi as api } from "../baseApi";
const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    getV2AdminOrganizationsByOrganizationIdDocumentReferencesAndDocumentReferenceId:
      build.query<
        GetV2AdminOrganizationsByOrganizationIdDocumentReferencesAndDocumentReferenceIdApiResponse,
        GetV2AdminOrganizationsByOrganizationIdDocumentReferencesAndDocumentReferenceIdApiArg
      >({
        query: (queryArg) => ({
          url: `/v2/admin/organizations/${queryArg.organizationId}/document-references/${queryArg.documentReferenceId}`,
        }),
      }),
    deleteV2ProviderDocumentReferencesByDocumentReferenceId: build.mutation<
      DeleteV2ProviderDocumentReferencesByDocumentReferenceIdApiResponse,
      DeleteV2ProviderDocumentReferencesByDocumentReferenceIdApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/document-references/${queryArg.documentReferenceId}`,
        method: "DELETE",
      }),
    }),
    getV2ProviderDocumentReferencesByDocumentReferenceId: build.query<
      GetV2ProviderDocumentReferencesByDocumentReferenceIdApiResponse,
      GetV2ProviderDocumentReferencesByDocumentReferenceIdApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/document-references/${queryArg.documentReferenceId}`,
      }),
    }),
    getV2ProviderPatientsByPatientIdDocumentReferences: build.query<
      GetV2ProviderPatientsByPatientIdDocumentReferencesApiResponse,
      GetV2ProviderPatientsByPatientIdDocumentReferencesApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/patients/${queryArg.patientId}/document-references`,
        params: {
          limit: queryArg.limit,
          offset: queryArg.offset,
          doc_kind: queryArg.docKind,
          document_domain: queryArg.documentDomain,
          sensitivity_level: queryArg.sensitivityLevel,
          encounter_id: queryArg.encounterId,
          source_artifact_id: queryArg.sourceArtifactId,
          date_from: queryArg.dateFrom,
          date_to: queryArg.dateTo,
          q: queryArg.q,
          sort: queryArg.sort,
        },
      }),
    }),
  }),
  overrideExisting: false,
});
export { injectedRtkApi as enhancedApi };
export type GetV2AdminOrganizationsByOrganizationIdDocumentReferencesAndDocumentReferenceIdApiResponse =
  /** status 200 OK */ DocumentReferenceDetailResponse;
export type GetV2AdminOrganizationsByOrganizationIdDocumentReferencesAndDocumentReferenceIdApiArg =
  {
    organizationId: string;
    documentReferenceId: string;
  };
export type DeleteV2ProviderDocumentReferencesByDocumentReferenceIdApiResponse =
  unknown;
export type DeleteV2ProviderDocumentReferencesByDocumentReferenceIdApiArg = {
  documentReferenceId: string;
};
export type GetV2ProviderDocumentReferencesByDocumentReferenceIdApiResponse =
  /** status 200 OK */ DocumentReferenceDetailResponse;
export type GetV2ProviderDocumentReferencesByDocumentReferenceIdApiArg = {
  documentReferenceId: string;
};
export type GetV2ProviderPatientsByPatientIdDocumentReferencesApiResponse =
  /** status 200 OK */ DocumentReferenceListResponse;
export type GetV2ProviderPatientsByPatientIdDocumentReferencesApiArg = {
  patientId: string;
  /** Max results per page */
  limit?: number;
  /** Offset for pagination */
  offset?: number;
  /** Filter by one or more doc_kinds (clinical_note, lab_report, imaging, intake_form, consent, roi, insurance_card, referral, prior_records_packet, legal_document, discharge_summary, neuropsych_eval, assessment, markdown_doc, form, other). Repeat the param for OR semantics. */
  docKind?: string[] | null;
  /** Filter by document domain. Defaults are caller-specific; known values are clinical and practice_operations. Repeat the param for OR semantics. */
  documentDomain?: ("clinical" | "practice_operations")[] | null;
  /** Filter by sensitivity level (unrestricted | phi_standard | sud_treatment | psychotherapy_note). */
  sensitivityLevel?: string[] | null;
  /** Restrict to records threaded under a specific encounter. */
  encounterId?: string | null;
  /** Restrict to records extracted from a specific artifact. */
  sourceArtifactId?: string | null;
  /** Inclusive lower bound on effective_date (authored_at when present, else created_at). */
  dateFrom?: string | null;
  /** Inclusive upper bound on effective_date. */
  dateTo?: string | null;
  /** Full-text search across title + description. Supports phrase quoting and boolean operators (Postgres websearch_to_tsquery). */
  q?: string | null;
  /** Ordering:
   * timeline (default) — authored_at when set, else created_at (the chart's natural chronological view).
   * uploaded_at_desc — purely created_at.
   * authored_at_desc — only authored_at, nulls last. */
  sort?: "timeline" | "uploaded_at_desc" | "authored_at_desc";
};
export type DocRefProcessingInfo = {
  /** Error message if enrichment failed, else None. */
  error?: string | null;
  /** When enrichment ran, ISO-8601 datetime string. */
  extracted_at?: string | null;
  /** Enrichment stages that completed successfully. */
  stages_completed?: string[];
};
export type DocRefSectionBBox = {
  /** Box height, fractional (0..1) of page height. */
  h: number;
  /** Page number the box is on (0-indexed). */
  page: number;
  /** Box width, fractional (0..1) of page width. */
  w: number;
  /** Left edge, fractional (0..1) of page width. */
  x: number;
  /** Top edge, fractional (0..1) of page height. */
  y: number;
};
export type DocRefSection = {
  /** Bounding-box regions for the section. */
  bboxes?: DocRefSectionBBox[];
  /** Last page the section spans. */
  page_end?: number | null;
  /** First page the section spans. */
  page_start?: number | null;
  /** Hint guiding retrieval/RAG over this section (e.g. what it answers). */
  retrieval_hint?: string | null;
  /** Stable section identifier within the document. */
  section_id: string;
  /** Short summary of the section's content. */
  summary?: string | null;
  /** Free-text tags classifying the section. */
  tags?: string[];
  /** Section heading/title. */
  title: string;
  /** Approximate token count of the section's content. */
  token_estimate?: number | null;
};
export type DocumentReferenceMetadata = {
  /** Questions this document can answer (retrieval-layer hints). */
  answerable_questions?: string[];
  /** Facility that authored the document, if known. */
  author_facility?: string | null;
  /** Name of the authoring provider, if known. */
  author_provider_name?: string | null;
  /** Catch-all for anything stashed beyond the shapes above. */
  extra?: {
    [key: string]: any;
  };
  /** Post-processor output keyed by doc_kind (e.g. 'insurance_card', 'lab_report'); values are flat dicts of extracted fields plus action state (acted_on_at/acted_on_by). Per-kind shape is owned by the post-processor. */
  extractions?: {
    [key: string]: {
      [key: string]: any;
    };
  };
  /** ISO 639-1 language code of the document content. */
  language?: string;
  /** Enrichment telemetry (errors, completed stages, timing). */
  processing?: DocRefProcessingInfo;
  /** Parsed sections of the document. */
  sections?: DocRefSection[];
  /** Free-text tags (diagnoses, medications, payers, document category). Filtered via the metadata->'tags' JSONB operator, not full-text search. */
  tags?: string[];
};
export type DocumentReferenceDetailResponse = {
  author_facility?: string | null;
  author_provider_name?: string | null;
  authored_at?: string | null;
  created_at: string;
  derived_via?: string | null;
  description?: string | null;
  doc_kind?: string | null;
  document_domain?: string;
  document_reference_id: string;
  effective_date?: string | null;
  encounter_id?: string | null;
  metadata?: DocumentReferenceMetadata;
  organization_id: string;
  patient_id?: string | null;
  processing_status: string;
  scope: string;
  sensitivity_level?: string | null;
  signed_at?: string | null;
  source_artifact_id?: string | null;
  source_location?: {
    [key: string]: any;
  } | null;
  supersedes_document_reference_id?: string | null;
  tags?: string[];
  title?: string | null;
  updated_at: string;
};
export type ClientErrorResponse = {
  /** The error code for the error */
  error_code?: string;
  /** The message for the error */
  message?: string;
  /** The HTTP status code for the client error */
  status?: number;
};
export type ValidationErrorElement = {
  ctx?: object;
  loc: string[];
  msg: string;
  type: string;
};
export type ValidationError = ValidationErrorElement[];
export type DocumentReferenceSummaryResponse = {
  author_facility?: string | null;
  author_provider_name?: string | null;
  authored_at?: string | null;
  created_at: string;
  derived_via?: string | null;
  description?: string | null;
  doc_kind?: string | null;
  document_domain?: string;
  document_reference_id: string;
  effective_date?: string | null;
  encounter_id?: string | null;
  organization_id: string;
  patient_id?: string | null;
  processing_status: string;
  scope: string;
  sensitivity_level?: string | null;
  signed_at?: string | null;
  source_artifact_id?: string | null;
  source_location?: {
    [key: string]: any;
  } | null;
  supersedes_document_reference_id?: string | null;
  tags?: string[];
  title?: string | null;
  updated_at: string;
};
export type DocumentReferenceListResponse = {
  items: DocumentReferenceSummaryResponse[];
  limit: number;
  offset: number;
  total: number;
};
